import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Box, Chip, CircularProgress, Fade } from "@mui/material";
import queryString from "query-string";
import TextField from "@mui/material/TextField";
import cloneDeep from "lodash.clonedeep";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CopyIcon from "@mui/icons-material/FileCopy";
import DownloadIcon from "@mui/icons-material/Download";
import Backdrop from "@mui/material/Backdrop";
import { useRecipeManager } from "../../context-providers/recipe-manager-provider";
import TitleBar from "../nav/title-bar";
import { useMealPlanner } from "../../context-providers/meal-planner-provider";
import LabelList from "../label/label-list";
import IngredientList from "../ingredient/ingredient-list";
import AddMealDialog from "../dialog/add-meal-dialog";
import { getRecipePhoto } from "../../util/image-helper";
import ImageUploader from "../input/image-uploader";
import IntentButton, { Intent } from "../input/intent-button";
import ROUTES from "../../routes/routes";
import RecipeField from "./recipe-field";
import RecipeMethod from "./recipe-method";
import { DEFAULT_FADE_ANIMATION } from "../../constants";
import ConfirmDeleteDialog from "../dialog/confirm-delete-dialog";
import { useFriendships } from "../../context-providers/friendships-provider";
import { useHome } from "../../context-providers/home-provider";
import usePreventReload from "../../hooks/use-prevent-reload";
import areRecipesEqual from "../../util/recipe-helper";
import type { Recipe } from "../../types";
import ImportRecipeDialog from "../dialog/import-recipe-dialog";
import RecipePreviewDialog from "../dialog/recipe-preview-dialog";
import type { ExtractedRecipe } from "../../types/recipe-import";
import { useSnackbar } from "notistack";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
  },

  buttons: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-evenly",
    position: "sticky",
    zIndex: 10,
    [theme.breakpoints.down("sm")]: {
      top: "54px",
    },
    [theme.breakpoints.up("sm")]: {
      top: "64px",
    },
  },

  button: {
    margin: "5px 0",
  },

  notes: {
    marginTop: "4px",
    padding: "4px",
    whiteSpace: "pre-wrap",
    width: "100%",
    minHeight: "30px",
  },

  fieldName: {
    fontWeight: "bold",
    fontSize: "16px",
  },

  recipeImgContainer: {
    background: theme.palette.grey.A100,
    position: "relative",
    height: "200px",
    overflow: "hidden",
  },

  recipeImg: {
    position: "relative",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "block",
    width: "100%",
    height: "auto",
  },

  photoButton: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },

  noPhotoText: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    color: theme.palette.grey["600"],
  },

  servesInput: {
    width: "120px",
    marginLeft: "10px",
  },

  emptyText: {
    color: theme.palette.grey.A200,
    padding: "10px 0",
  },

  sharedBy: {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "calc(100% - 80px)",
    overflow: "hidden",
  },
}));

export default function Recipe() {
  const { classes, cx } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    getUserRecipe,
    getHomeRecipe,
    saveRecipe,
    removeRecipe,
    getFriendsRecipe,
    addRecipe,
  } = useRecipeManager();
  const { addUserMeal, addHomeMeal } = useMealPlanner();
  const { friendships } = useFriendships();
  const { home } = useHome();
  const { recipeId, username } = useParams();
  const [showAddMealDialog, setShowAddMealDialog] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [carefullyCancel, setCarefullyCancel] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [extractedRecipe, setExtractedRecipe] =
    useState<ExtractedRecipe | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const query = queryString.parse(location.search);
  const editMode = query.edit === "true";
  const isHomeRecipe = query.homeRecipe === "true";
  const isHome = home && !!home.id;

  const addMeal = isHome ? addHomeMeal : addUserMeal;
  const getRecipe = isHomeRecipe ? getHomeRecipe : getUserRecipe;

  let recipe: Recipe | null = null;
  if (!username) {
    recipe = getRecipe(recipeId);
  }

  const [pendingRecipe, setPendingRecipe] = useState<Recipe | null>(recipe);
  const isShared = username || isHomeRecipe;
  const changesMade = !areRecipesEqual(recipe, pendingRecipe);

  usePreventReload(changesMade);

  // Update pendingRecipe when recipe loads from context
  useEffect(() => {
    if (recipe && !pendingRecipe && !username) {
      setPendingRecipe(recipe);
    }
  }, [recipe, pendingRecipe, username]);

  useEffect(() => {
    if (carefullyCancel) {
      setCarefullyCancel(false);
      navigate(location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carefullyCancel]);

  useEffect(() => {
    if (!isShared && !editMode && changesMade) {
      setPendingRecipe(getRecipe(recipeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, recipe]);

  useEffect(() => {
    if (username && friendships) {
      const friend = friendships.find((f: any) => f.username === username);
      if (friend?.id) {
        getFriendsRecipe(friend.id, recipeId).then(setPendingRecipe);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show loading state while data is being fetched
  if (!pendingRecipe || Object.keys(pendingRecipe).length === 0) {
    return (
      <Box className={classes.root}>
        <TitleBar title="Loading..." onBack={() => navigate(ROUTES.RECIPES)} />
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (editMode && isShared) {
    navigate(location.pathname);
  }

  function addMealToPlan(mealToAdd: any) {
    addMeal(mealToAdd).then(() => {
      setShowAddMealDialog(false);
    });
  }

  function openAddMealDialog() {
    setShowAddMealDialog(true);
  }

  function discardPendingRecipe() {
    setCarefullyCancel(true);
  }

  function savePendingRecipe() {
    const updatedRecipe = {
      ...pendingRecipe,
      name: pendingRecipe.name.trim(),
    };
    saveRecipe(cloneDeep(updatedRecipe));
    navigate(location.pathname);
  }

  function copyRecipe() {
    addRecipe(pendingRecipe); // TODO: Check for duplication and warn user, allow overwrite
  }

  function onPropChange(propName: any, value: any) {
    setPendingRecipe({
      ...pendingRecipe,
      [propName]: value,
    });
  }

  function onRecipeDelete() {
    if (recipe?.recipeId) {
      removeRecipe(recipe.recipeId);
    }
    navigate("/recipes");
  }

  function getFriendUsername(id: any) {
    return friendships?.find((f: any) => f.id === id)?.username;
  }

  function handleImportComplete(extracted: ExtractedRecipe) {
    setExtractedRecipe(extracted);
    setShowImportDialog(false);
    setShowPreviewDialog(true);
  }

  function handlePreviewAccept(importedRecipe: Recipe) {
    // Merge imported data with current pendingRecipe, preserving the recipe ID
    setPendingRecipe({
      ...pendingRecipe,
      ...importedRecipe,
      recipeId: pendingRecipe?.recipeId, // Preserve ID
    });
    setShowPreviewDialog(false);
    setExtractedRecipe(null);
    enqueueSnackbar("Recipe imported successfully!", { variant: "success" });
  }

  function handlePreviewReject() {
    setShowPreviewDialog(false);
    setExtractedRecipe(null);
    setShowImportDialog(true); // Reopen to try again
  }

  const servesValid =
    pendingRecipe.serves &&
    pendingRecipe.serves > 0 &&
    Number.isInteger(pendingRecipe.serves);

  return (
    <Box key={editMode ? "recipeEdit" : "recipeView"} className={classes.root}>
      <TitleBar
        title={pendingRecipe.name}
        editMode={editMode}
        setTitle={(name) => onPropChange("name", name)}
        onBack={() => navigate("/recipes")}
      />
      <Fade in timeout={DEFAULT_FADE_ANIMATION}>
        <Box>
          <Box className={classes.recipeImgContainer}>
            {pendingRecipe.img ? (
              <img
                className={classes.recipeImg}
                src={getRecipePhoto(pendingRecipe.img)}
                alt={pendingRecipe.name}
              />
            ) : (
              <Typography className={classes.noPhotoText}>No Photo</Typography>
            )}
            {editMode && (
              <ImageUploader
                className={classes.photoButton}
                buttonText={`${pendingRecipe.img ? "Edit" : "Add"} photo`}
                onChange={(img) => onPropChange("img", img)}
              />
            )}
          </Box>
          {editMode ? (
            <Box className={classes.buttons}>
              <IntentButton
                className={classes.button}
                onClick={() => setShowImportDialog(true)}
                variant="contained"
                intent={Intent.TERTIARY}
                startIcon={<DownloadIcon />}
              >
                Import Recipe
              </IntentButton>
              <IntentButton
                className={classes.button}
                disabled={!servesValid || !changesMade}
                onClick={() => savePendingRecipe()}
                variant="contained"
                intent={Intent.PRIMARY}
                startIcon={<SaveIcon />}
              >
                Save
              </IntentButton>
              <IntentButton
                className={classes.button}
                onClick={() => discardPendingRecipe()}
                variant="contained"
                intent={Intent.TERTIARY}
                startIcon={<CancelIcon />}
              >
                Cancel
              </IntentButton>
            </Box>
          ) : (
            <Box className={classes.buttons}>
              {!isShared && (
                <>
                  <IntentButton
                    className={classes.button}
                    onClick={() => navigate(`${location.pathname}?edit=true`)}
                    variant="contained"
                    intent={Intent.PRIMARY}
                    startIcon={<EditIcon fontSize="small" />}
                  >
                    Edit
                  </IntentButton>
                  <IntentButton
                    className={classes.button}
                    onClick={() => {
                      setSelectedRecipe(pendingRecipe);
                      setShowDeleteDialog(true);
                    }}
                    variant="contained"
                    intent={Intent.ERROR}
                    startIcon={<DeleteIcon />}
                  >
                    Delete
                  </IntentButton>
                </>
              )}
              {username && (
                <IntentButton
                  className={classes.button}
                  onClick={copyRecipe}
                  variant="contained"
                  intent={Intent.PRIMARY}
                  startIcon={<CopyIcon />}
                >
                  Copy to Recipes
                </IntentButton>
              )}
              <IntentButton
                className={classes.button}
                onClick={openAddMealDialog}
                variant="contained"
                intent={Intent.SECONDARY}
                startIcon={<AddIcon />}
              >
                Add to Plan
              </IntentButton>
            </Box>
          )}
          {isShared && (
            <RecipeField name="Created By" inline>
              <Typography className={classes.sharedBy}>
                &nbsp;
                {username ||
                  (recipe?.sharedFrom
                    ? getFriendUsername(recipe.sharedFrom)
                    : "")}
              </Typography>
            </RecipeField>
          )}
          <RecipeField name="Labels" inline>
            <LabelList
              recipe={pendingRecipe}
              setRecipe={setPendingRecipe}
              editMode={editMode}
            />
          </RecipeField>
          <RecipeField name="Serves" inline>
            {editMode ? (
              <TextField
                id="standard-number"
                className={classes.servesInput}
                type="number"
                value={pendingRecipe.serves}
                onChange={(event: any) =>
                  setPendingRecipe({
                    ...pendingRecipe,
                    serves: parseInt(event.target.value, 10),
                  })
                }
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{ min: "1", step: "1" }}
                error={!servesValid}
              />
            ) : (
              <Typography>&nbsp;{pendingRecipe.serves}</Typography>
            )}
          </RecipeField>
          <RecipeField name="Calories (per serving)" inline>
            {editMode ? (
              <TextField
                className={classes.servesInput}
                type="number"
                value={pendingRecipe.calories ?? ""}
                onChange={(event: any) =>
                  setPendingRecipe({
                    ...pendingRecipe,
                    calories: event.target.value
                      ? parseInt(event.target.value, 10)
                      : undefined,
                    caloriesEstimated: false,
                  })
                }
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{ min: "0", step: "1" }}
                placeholder="Not specified"
              />
            ) : (
              <Typography>
                &nbsp;{pendingRecipe.calories ?? "Not specified"}
              </Typography>
            )}
            {pendingRecipe.calories != null &&
              pendingRecipe.caloriesEstimated && (
                <Chip
                  label="Estimated"
                  size="small"
                  color="info"
                  sx={{ ml: 1 }}
                />
              )}
          </RecipeField>
          {!editMode && pendingRecipe.timesAddedAsMeal > 0 && (
            <RecipeField name="Times used" inline>
              <Typography>&nbsp;{pendingRecipe.timesAddedAsMeal}</Typography>
            </RecipeField>
          )}
          <RecipeField name="Ingredients">
            <IngredientList
              ingredients={pendingRecipe.ingredients}
              setIngredients={(ingredients) =>
                onPropChange("ingredients", ingredients)
              }
              editMode={editMode}
            />
          </RecipeField>
          <RecipeField name="Method">
            <RecipeMethod
              method={pendingRecipe.method}
              setMethod={(method) => onPropChange("method", method)}
              editMode={editMode}
            />
          </RecipeField>
          <RecipeField name="Notes">
            {editMode ? (
              <TextField
                label="Notes"
                multiline
                fullWidth
                variant="outlined"
                value={pendingRecipe.notes ? pendingRecipe.notes : ""}
                onChange={(event: any) =>
                  setPendingRecipe({
                    ...pendingRecipe,
                    notes: event.target.value,
                  })
                }
              />
            ) : (
              <Typography
                className={cx(classes.notes, {
                  [classes.emptyText]: !pendingRecipe.notes,
                })}
                variant="body1"
                align="center"
              >
                {pendingRecipe.notes ? pendingRecipe.notes : "No Notes"}
              </Typography>
            )}
          </RecipeField>
          <Box id="bottom-shim" style={{ height: "90px" }} />
          <AddMealDialog
            open={showAddMealDialog}
            handleClose={() => setShowAddMealDialog(false)}
            recipe={recipe}
            addMeal={addMealToPlan}
          />
          <ConfirmDeleteDialog
            open={showDeleteDialog}
            handleClose={() => setShowDeleteDialog(false)}
            item={selectedRecipe}
            onDelete={onRecipeDelete}
            idProp="recipeId"
            itemType="Recipe"
          />
          <ImportRecipeDialog
            open={showImportDialog}
            handleClose={() => setShowImportDialog(false)}
            onImportComplete={handleImportComplete}
          />
          <RecipePreviewDialog
            open={showPreviewDialog}
            handleClose={() => setShowPreviewDialog(false)}
            extractedRecipe={extractedRecipe}
            onAccept={handlePreviewAccept}
            onReject={handlePreviewReject}
          />
          {/* TODO: React Router v6 removed Prompt component. Need to implement useBlocker hook or similar for navigation blocking */}
          {/* <Prompt
                        message={(loc: any, action: any) => (editMode && changesMade && ((action === 'POP' || carefullyCancel || location.pathname !== loc.pathname))
                            ? 'Are you sure you want to leave this page? You have unsaved changes'
                            : true)}
                    /> */}
        </Box>
      </Fade>
    </Box>
  );
}
